import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { Marca } from "@notes/ui";
import {
  Botao, MenuSuspenso, ProvedorDeDicas, DicaDeTela, Paleta, usarPaleta, Avatar, Esqueleto,
  type ComandoDaPaleta, type ItemDeMenu,
} from "@notes/ui";
import {
  CalendarClock, ListChecks, Mic, Settings, ShieldCheck, LogOut, Sun, Moon, Users, Search,
  Video, ExternalLink, ChevronDown, Menu as Hamburguer, X, Building2, Sparkles, KeyRound, Home, Phone,
} from "lucide-react";
import { useEu, usePode } from "./Sessao.js";
import { alternarTema, temaAtual } from "../lib/tema.js";
import { usarPendencias, usarPontePronta, usarReunioes, abrirNoCrm, mensagemDeErro } from "../lib/api.js";

/**
 * O SHELL. Barra superior, como no CRM e no MKT da casa.
 *
 * Por que superior e não lateral: a tela do Notes é de LEITURA densa (linha do
 * tempo, transcrição, tabela), e cada pixel de largura vale. O menu lateral
 * comia 240px em toda tela para mostrar oito itens que a pessoa visita duas
 * vezes por dia.
 *
 * O grupo "Inteligência" abre em lista; o que a pessoa usa o tempo todo (Hoje,
 * Reuniões, Gravar, Perguntar) fica em atalho direto. O que ela não pode abrir
 * não aparece, em vez de aparecer e dar erro no clique.
 *
 * "Segurança" é do menu do PERFIL, e não da barra: ela é da pessoa, não da
 * agência. Mesmo lugar do EnturOS CRM.
 */

type Destino = { para: string; rotulo: string; icone: typeof Mic; contador?: number | undefined; descricao?: string };

export function Shell({ children }: { children: React.ReactNode }) {
  const eu = useEu();
  const navegar = useNavigate();
  const rota = useRouterState({ select: (s) => s.location.pathname });
  const [tema, setTema] = useState(temaAtual());
  const [menuAberto, setMenuAberto] = useState(false);
  const paleta = usarPaleta();

  const { data: pendencias } = usarPendencias();
  const { data: ponte } = usarPontePronta();
  const reunioes = usarReunioes();

  const podeAprovar = usePode("acoes.aprovar");
  const podeVerEquipe = usePode("score.ler_equipe");
  const podeGerirPonte = usePode("ponte.gerir");
  // A telefonia é opcional POR AGÊNCIA: a permissão só existe com o módulo
  // ligado, então quem não tem o Voice não vê um item de menu que não leva a
  // lugar nenhum.
  const temVoz = usePode("voz.ler");

  const [erroCrm, setErroCrm] = useState<string | null>(null);
  const irParaOCrm = async () => {
    setErroCrm(null);
    try { await abrirNoCrm({ tipo: "home" }); } catch (e) { setErroCrm(mensagemDeErro(e)); }
  };

  // Fecha a gaveta do celular ao trocar de tela: menu aberto por cima do
  // conteúdo novo é o clássico "cliquei e não aconteceu nada".
  useEffect(() => { setMenuAberto(false); }, [rota]);

  const conversas: Destino[] = [
    { para: "/", rotulo: "Hoje", icone: Home, descricao: "o seu dia" },
    { para: "/reunioes", rotulo: "Reuniões", icone: Mic, descricao: "tudo que foi ouvido" },
    ...(temVoz ? [{ para: "/ligacoes", rotulo: "Ligações", icone: Phone, descricao: "quem falou por telefone" }] : []),
    { para: "/perguntar", rotulo: "Perguntar", icone: Search, descricao: "pergunte ao acervo" },
  ];
  const inteligencia: Destino[] = [
    ...(podeVerEquipe ? [{ para: "/equipe", rotulo: "Equipe", icone: Users, descricao: "como a agência conversa" }] : []),
    ...(podeAprovar ? [{ para: "/aprovacoes", rotulo: "Aprovações", icone: ShieldCheck,
      contador: pendencias?.acoesParaAprovar.length, descricao: "o que espera você" }] : []),
    { para: "/pendencias", rotulo: "Pendências", icone: ListChecks, contador: pendencias?.promessas.length, descricao: "o que foi prometido" },
  ];
  // "Gravar" era o segundo item do grupo "Assessor". Com o assessor por
  // WhatsApp fora do produto (08/09/2026), o grupo de um item só deixou de
  // fazer sentido: ele vira atalho direto, ao lado de "Reuniões".
  const gravar: Destino = { para: "/gravar", rotulo: "Gravar", icone: Video, descricao: "aparelhos e captura" };

  const todos = [...conversas, ...inteligencia, gravar,
    ...(podeGerirPonte ? [{ para: "/configuracoes", rotulo: "Configurações", icone: Settings } as Destino] : [])];

  const comandos: ComandoDaPaleta[] = useMemo(() => [
    ...todos.map((d) => ({
      id: `ir:${d.para}`, grupo: "Ir para", rotulo: d.rotulo, detalhe: d.descricao,
      icone: <d.icone size={15} />, aoEscolher: () => void navegar({ to: d.para }),
    })),
    ...(reunioes.data?.reunioes ?? []).slice(0, 40).map((r) => ({
      id: `reuniao:${r.id}`, grupo: "Reuniões", rotulo: r.titulo,
      detalhe: new Date(r.inicioEm).toLocaleDateString("pt-BR", { day: "2-digit", month: "short" }),
      icone: <Mic size={15} />,
      aoEscolher: () => void navegar({ to: "/reunioes/$id", params: { id: r.id } }),
    })),
    ...(ponte?.ativa ? [{
      id: "crm", grupo: "Atalhos", rotulo: "Abrir o EnturOS CRM",
      detalhe: ponte.crmTenantNome ?? undefined, icone: <ExternalLink size={15} />,
      aoEscolher: () => void irParaOCrm(),
    }] : []),
    {
      id: "tema", grupo: "Atalhos", rotulo: tema === "escuro" ? "Usar o tema claro" : "Usar o tema escuro",
      icone: tema === "escuro" ? <Sun size={15} /> : <Moon size={15} />,
      aoEscolher: () => setTema(alternarTema()),
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [reunioes.data, ponte, tema, podeAprovar, podeVerEquipe, podeGerirPonte, temVoz, pendencias]);

  const agenciaAtual = eu.agencias.find((a) => a.id === eu.agenciaAtual);
  const trocarAgencia = (id: string) => {
    // A agência escolhida vai no cabeçalho de toda chamada; o servidor CONFERE
    // se a pessoa participa dela. Recarregar é o caminho honesto: metade do
    // cache é da agência anterior.
    localStorage.setItem("enotes:agencia", id);
    location.assign("/");
  };

  const itensDaConta: ItemDeMenu[] = [
    ...(eu.agencias.length > 1
      ? eu.agencias.map((a) => ({
          rotulo: a.nome, icone: <Building2 size={15} />, ativo: a.id === eu.agenciaAtual,
          aoEscolher: () => trocarAgencia(a.id),
        }))
      : []),
    // Segurança é da PESSOA, e não da agência: por isso mora aqui, no menu do
    // perfil, e não em Configurações, que é onde a agência se ajusta. É o mesmo
    // lugar do EnturOS CRM.
    {
      rotulo: "Segurança", icone: <ShieldCheck size={15} />, separadorAntes: eu.agencias.length > 1,
      aoEscolher: () => void navegar({ to: "/seguranca" }),
    },
    {
      rotulo: tema === "escuro" ? "Tema claro" : "Tema escuro",
      icone: tema === "escuro" ? <Sun size={15} /> : <Moon size={15} />,
      aoEscolher: () => setTema(alternarTema()),
    },
    ...(podeGerirPonte ? [{ rotulo: "Configurações", icone: <Settings size={15} />, aoEscolher: () => void navegar({ to: "/configuracoes" }) }] : []),
    { rotulo: "Sair", icone: <LogOut size={15} />, perigo: true, separadorAntes: true, aoEscolher: () => void sair() },
  ];

  return (
    <ProvedorDeDicas>
      <div className="enotes enotes-app">
        <header className="enotes-barra">
          <div className="enotes-barra-esq">
            <Link to="/" className="enotes-barra-marca" aria-label="EnturOS Notes, ir para o início">
              <Marca tamanho={22} />
            </Link>

            <nav className="enotes-nav" aria-label="Seções">
              <AtalhoDireto destino={conversas[0]!} rota={rota} />
              <AtalhoDireto destino={conversas[1]!} rota={rota} />
              <GrupoDeMenu rotulo="Inteligência" destinos={inteligencia} rota={rota} navegar={navegar} />
              <AtalhoDireto destino={gravar} rota={rota} />
              <AtalhoDireto destino={conversas[2]!} rota={rota} />
            </nav>
          </div>

          <div className="enotes-barra-dir">
            <button className="enotes-busca" onClick={paleta.abrir} aria-label="Buscar (atalho: control ou command mais K)">
              <Search size={14} aria-hidden />
              <span className="enotes-busca-texto">Buscar</span>
              <kbd className="enotes-atalho">⌘K</kbd>
            </button>

            {ponte?.ativa && (
              <DicaDeTela texto={`Abrir o EnturOS CRM${ponte.crmTenantNome ? ` (${ponte.crmTenantNome})` : ""}, mesma conta, sem novo login`}>
                <button className="enotes-barra-botao" onClick={() => void irParaOCrm()}>
                  <ExternalLink size={15} aria-hidden />
                  <span className="enotes-so-largo">CRM</span>
                </button>
              </DicaDeTela>
            )}

            {podeGerirPonte && (
              <DicaDeTela texto="Configurações">
                <Link to="/configuracoes" className="enotes-barra-botao" aria-label="Configurações">
                  <Settings size={15} aria-hidden />
                </Link>
              </DicaDeTela>
            )}

            <MenuSuspenso
              rotuloDoGrupo={agenciaAtual?.nome ?? "Sua conta"}
              itens={itensDaConta}
              alinhamento="end"
              gatilho={
                <button className="enotes-conta" aria-label={`Conta de ${eu.nome ?? "usuário"}`}>
                  <Avatar nome={eu.nome ?? "?"} tamanho={26} />
                  <ChevronDown size={13} aria-hidden className="enotes-so-largo" />
                </button>
              }
            />

            <button className="enotes-barra-botao enotes-so-estreito" onClick={() => setMenuAberto((v) => !v)}
              aria-label={menuAberto ? "Fechar o menu" : "Abrir o menu"} aria-expanded={menuAberto}>
              {menuAberto ? <X size={17} /> : <Hamburguer size={17} />}
            </button>
          </div>
        </header>

        {eu.segundoFatorExigido && (
          <div className="enotes-faixa" role="status">
            <span aria-hidden className="sinal sinal-atencao" />
            O EnturOS CRM exige verificação em duas etapas. Ative a sua aqui também, em Configurações.
          </div>
        )}
        {erroCrm && (
          <div className="enotes-faixa" data-tom="critico" role="alert">
            <span aria-hidden className="sinal sinal-critico" />{erroCrm}
          </div>
        )}

        {menuAberto && (
          <nav className="enotes-gaveta-menu" aria-label="Menu">
            {[...conversas, ...inteligencia, gravar,
              ...(podeGerirPonte ? [{ para: "/configuracoes", rotulo: "Configurações", icone: Settings } as Destino] : [])]
              .map((d) => (
                <Link key={d.para} to={d.para} className="enotes-gaveta-item"
                  data-ativo={ehAtivo(rota, d.para) || undefined}>
                  <d.icone size={16} aria-hidden />
                  <span>{d.rotulo}</span>
                  {d.contador ? <span className="enotes-contador num">{d.contador}</span> : null}
                </Link>
              ))}
          </nav>
        )}

        <main className="enotes-conteudo">{children}</main>

        <Paleta aberta={paleta.aberta} aoFechar={paleta.fechar} comandos={comandos}
          textoVazio="Nada com esse nome. Para procurar DENTRO das conversas, use a tela Perguntar." />
      </div>
    </ProvedorDeDicas>
  );
}

async function sair() {
  await fetch("/auth/sair", { method: "POST" });
  location.reload();
}

const ehAtivo = (rota: string, para: string) => (para === "/" ? rota === "/" : rota.startsWith(para));

function AtalhoDireto({ destino, rota }: { destino: Destino; rota: string }) {
  const Icone = destino.icone;
  return (
    <Link to={destino.para} className="enotes-nav-item" data-ativo={ehAtivo(rota, destino.para) || undefined}>
      <Icone size={15} aria-hidden />
      <span>{destino.rotulo}</span>
      {destino.contador ? <span className="enotes-contador num">{destino.contador}</span> : null}
    </Link>
  );
}

function GrupoDeMenu({ rotulo, destinos, rota, navegar }: {
  rotulo: string; destinos: Destino[]; rota: string; navegar: ReturnType<typeof useNavigate>;
}) {
  if (destinos.length === 0) return null;
  const algumAtivo = destinos.some((d) => ehAtivo(rota, d.para));
  const pendentes = destinos.reduce((n, d) => n + (d.contador ?? 0), 0);
  return (
    <MenuSuspenso
      rotuloDoGrupo={rotulo}
      itens={destinos.map((d) => ({
        rotulo: d.rotulo, icone: <d.icone size={15} />, ativo: ehAtivo(rota, d.para),
        atalho: d.contador ? String(d.contador) : undefined,
        aoEscolher: () => void navegar({ to: d.para }),
      }))}
      gatilho={
        <button className="enotes-nav-item" data-ativo={algumAtivo || undefined}>
          <span>{rotulo}</span>
          {pendentes > 0 && <span className="enotes-contador num">{pendentes}</span>}
          <ChevronDown size={13} aria-hidden />
        </button>
      }
    />
  );
}

// ---------------------------------------------------------------------------
// A moldura de página. Título, respiro e largura máxima ficam AQUI: tela que
// define a própria margem é como oito telas ficam com oito margens diferentes.
// ---------------------------------------------------------------------------
export function Pagina({ titulo, descricao, acao, largura = "normal", children }: {
  titulo: string; descricao?: React.ReactNode; acao?: React.ReactNode;
  largura?: "normal" | "larga" | "estreita"; children: React.ReactNode;
}) {
  return (
    <div className="enotes-pagina" data-largura={largura}>
      <header className="enotes-phead">
        <div className="enotes-phead-texto">
          <h1 className="titulo-pagina">{titulo}</h1>
          {descricao && <p className="enotes-phead-sub">{descricao}</p>}
        </div>
        {acao && <div className="enotes-phead-acao">{acao}</div>}
      </header>
      {children}
    </div>
  );
}

/** Seção com título pequeno. O `surge` escalona a entrada, de cima para baixo. */
export function Secao({ titulo, acao, children, ordem = 0, className }: {
  titulo?: string; acao?: React.ReactNode; children: React.ReactNode; ordem?: number; className?: string;
}) {
  return (
    <section className={`enotes-secao surge ${className ?? ""}`} style={{ animationDelay: `${ordem * 55}ms` }}>
      {(titulo || acao) && (
        <div className="enotes-secao-topo">
          {titulo && <p className="eyebrow">{titulo}</p>}
          {acao}
        </div>
      )}
      {children}
    </section>
  );
}

/** Faixa de números. Um número sem o que ele significa não é informação. */
export function Numeros({ itens }: {
  itens: Array<{
    rotulo: string; valor: React.ReactNode;
    nota?: string | undefined; alerta?: boolean | undefined; icone?: React.ReactNode | undefined;
  }>;
}) {
  return (
    <ul className="enotes-numeros">
      {itens.map((n, i) => (
        <li key={n.rotulo} className="enotes-numero surge" style={{ animationDelay: `${i * 45}ms` }}>
          <span className="enotes-numero-topo">
            {n.icone && <span className="enotes-numero-icone" aria-hidden>{n.icone}</span>}
            <span className="carimbo">{n.rotulo}</span>
          </span>
          <span className="enotes-numero-valor num" data-alerta={n.alerta || undefined}>{n.valor}</span>
          {n.nota && <span className="enotes-numero-nota">{n.nota}</span>}
        </li>
      ))}
    </ul>
  );
}

export { Vazio } from "@notes/ui";

/** A espera com a FORMA do conteúdo: sem pulo quando o dado chega. */
export function Carregando({ tipo = "lista" }: { tipo?: "lista" | "cartoes" | "tabela" | "texto" }) {
  if (tipo === "cartoes") {
    return (
      <div className="enotes-cartoes" aria-busy="true">
        {[0, 1, 2].map((i) => (
          <div key={i} className="enotes-cartao"><Esqueleto linhas={3} /></div>
        ))}
      </div>
    );
  }
  if (tipo === "tabela") {
    return <div className="enotes-cartao" aria-busy="true"><Esqueleto linhas={6} altura="1.1rem" /></div>;
  }
  if (tipo === "texto") return <div aria-busy="true"><Esqueleto linhas={4} /></div>;
  return <div className="enotes-cartao" aria-busy="true"><Esqueleto linhas={4} /></div>;
}

export function Erro({ mensagem, aoTentarDeNovo }: { mensagem: string; aoTentarDeNovo?: () => void }) {
  return (
    <div className="enotes-erro-caixa" role="alert">
      <p className="enotes-erro-titulo">
        <span aria-hidden className="sinal sinal-critico" /> {mensagem}
      </p>
      {aoTentarDeNovo && <Botao tamanho="sm" onClick={aoTentarDeNovo}>Tentar de novo</Botao>}
    </div>
  );
}
